import { CSSProperties, useEffect, useState } from 'react';
import { Color, Size, Styles } from '@vivli/shared/theme';
import { useDataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { usePublishApproved } from '@vivli/features/data-requests/infrastructure/hook';
import { rightHeaderContainerStyle, TabComponent, TabsComponent } from '@vivli/shared/components';
import { useFormContext } from 'react-hook-form';
import { IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { PublishingConfigFormComponent, PublishingContentFormComponent } from '@vivli/features/data-requests/components';
import { AssetsConstant } from '@vivli/shared/infrastructure/constants';
import { PublishedStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { ButtonView } from '@vivli/shared/views';

const containerStyle: CSSProperties = {
    margin: `0 ${Size.INSET_SIZE}px`,
    height: '110%'
};

const formRowContainerStyle: CSSProperties = {
    paddingLeft: 0,
    paddingRight: 0,
    paddingBottom: 0,
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'repeat(1fr)',
    rowGap: '20px'
};

const buttonContainerStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '40px',
    marginLeft: Size.INSET_SIZE
};

const tabButtonsStyle: CSSProperties = {
    display: 'flex',
    gap: '10px',
    height: '100%',
    position: 'absolute',
    right: '30px'
};

const rejectButtonStyle: CSSProperties = {
    backgroundColor: 'white',
    backgroundImage: `url(${AssetsConstant.RED_X_ICON})`,
    backgroundPosition: 'center left 15px',
    backgroundRepeat: 'no-repeat',
    borderBottom: '1px solid',
    borderLeft: '1px solid',
    borderRight: '1px solid',
    borderTop: '1px solid',
    borderColor: Color.ERROR_RED,
    boxShadow: 'none',
    color: Color.ERROR_RED,
    marginRight: 10,
    maxHeight: 40,
    paddingLeft: 30
};

interface IPublishApprovedTab {
    name: string;
    sortOrder: number;
    title: string;
}

export const DataRequestPublishApprovedComponent = () => {
    const { dataRequest, handleFormSavePublish, handlePublishingSaveWithSecondaryDoi, isFormDirty, isSavingDataRequest } =
        useDataRequestContext();
    const formApi = useFormContext<IDataRequest>();
    const isCurrentlyPublished = dataRequest.publishing.publishingRules.publishedStatus === PublishedStatusEnum.Published;
    const [publishedPage, setPublishedPage] = useState<boolean>(isCurrentlyPublished);

    const { getPublishedText } = usePublishApproved();
    const publishedText = getPublishedText(dataRequest);

    enum TabTitleEnum {
        approvedRequest = ''
    }

    enum TabRouteEnum {
        approvedRequest = 'Approved Request'
    }

    const getTabs = () => {
        const defaultTabs: IPublishApprovedTab[] = [
            {
                name: TabRouteEnum.approvedRequest,
                sortOrder: 0,
                title: TabTitleEnum.approvedRequest
            }
        ];
        return defaultTabs;
    };

    const [tabs, setTabs] = useState<IPublishApprovedTab[]>();
    const [activeTab, setActiveTab] = useState('');
    const handleTabClick = (tab: IPublishApprovedTab) => {
        setActiveTab(tab.name);
    };

    useEffect(() => {
        formApi.trigger().then((r) => {
            setTabs([getTabs()[0]]);
        });
        setTabs([getTabs()[0]]);
    }, []);

    useEffect(() => {
        if (tabs !== undefined) setActiveTab(tabs[0].name);
    }, [tabs]);

    const onPublishClick = (nowPublished: boolean) => {
        let updatedDataRequest = dataRequest;
        if (nowPublished) {
            updatedDataRequest.publishing.publishingRules.publishedStatus = PublishedStatusEnum.Published;
        } else {
            updatedDataRequest.publishing.publishingRules.publishedStatus = PublishedStatusEnum.NotPublished;
        }

        formApi.setValue(`publishing.publishingRules.publishedStatus`, updatedDataRequest.publishing.publishingRules.publishedStatus);
        formApi.trigger().then((r) => {
            handleFormSavePublish(false);
        });

        setPublishedPage(nowPublished);
    };

    const onSaveClick = () => {
        formApi.trigger().then((r) => {
            handleFormSavePublish(false);
        });
    };

    const onDoiClick = () => {
        handlePublishingSaveWithSecondaryDoi();
    };

    const onPreviewClick = async () => {
        await formApi.trigger();
        await handleFormSavePublish(false);
        window.open(`/admindatarequests/${dataRequest.id}/ApprovedRequestsPublishing/Preview`);
    };

    const buttonsDisplayed = (activeTab) => {
        switch (activeTab) {
            case 'Approved Request':
                return (
                    <div style={rightHeaderContainerStyle}>
                        <div style={buttonContainerStyle}>
                            <ButtonView
                                data-test-id="unpublish-approved-request-button"
                                style={rejectButtonStyle}
                                disabled={!publishedPage}
                                onClick={() => onPublishClick(false)}
                            >
                                Unpublish
                            </ButtonView>
                        </div>
                        <div style={buttonContainerStyle}>
                            <ButtonView
                                data-test-id="publish-approved-request-button"
                                style={Styles.Button.BUTTON_DARKER_GREEN_CHECK}
                                disabled={publishedPage}
                                onClick={() => onPublishClick(true)}
                            >
                                Publish
                            </ButtonView>
                        </div>
                        <div style={buttonContainerStyle}>
                            <ButtonView
                                data-test-id="preview-approved-request-button"
                                style={{ maxWidth: '200px' }}
                                onClick={onPreviewClick}
                            >
                                Preview
                            </ButtonView>
                        </div>
                        <div style={buttonContainerStyle}>
                            <ButtonView data-test-id="doi-update-or-create-button" style={{ maxWidth: '200px' }} onClick={onDoiClick}>
                                Create or Update DOI
                            </ButtonView>
                        </div>
                        <div style={buttonContainerStyle}>
                            <ButtonView
                                data-test-id="save-approved-request-button"
                                style={{ maxWidth: '200px' }}
                                onClick={onSaveClick}
                                isLoading={isSavingDataRequest}
                                disabled={!isFormDirty || isSavingDataRequest}
                            >
                                Save
                            </ButtonView>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    const tabDisplayed = (activeTab) => {
        switch (activeTab) {
            case 'Approved Request':
                return (
                    <div style={formRowContainerStyle}>
                        <PublishingContentFormComponent dataRequest={dataRequest} isConfigMode={true} />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div style={containerStyle}>
            <TabsComponent
                defaultTab={0}
                buttonsOuterStyleOverride={{ width: '100%', marginBottom: '-5' }}
                subheader={<PublishingConfigFormComponent dataRequest={dataRequest} />}
            >
                {tabs?.map((tab, i) => {
                    const { title } = tab;
                    return (
                        <TabComponent
                            key={i}
                            title={publishedText}
                            onTabClick={() => handleTabClick(tab)}
                            rightHeaderContent={<div style={tabButtonsStyle}>{buttonsDisplayed(activeTab)}</div>}
                        >
                            {tabDisplayed(activeTab)}
                        </TabComponent>
                    );
                })}
            </TabsComponent>
        </div>
    );
};
